import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Typography, CircularProgress } from '@mui/material'
import { ApolloError, useMutation } from '@apollo/client'
import {
  ICreateOrigoAccountData,
  ICreateOrigoAccountVars,
  CREATE_ORIGO_ACCOUNT,
} from '../../graphql/hid/createOrigoAccount'
import { toast } from 'react-toastify'
import { IUserDataRow } from './HIDUsersTable'
import Error from '../Shared/Error'
import { colors } from '../../const/colors'
import CardSettingsForm from './CardSettingsForm'
import { useForm } from 'react-hook-form'
import { getBase64 } from '../../utils/getBase64'
import AddIcon from '@mui/icons-material/Add'
import HourglassEmptyOutlinedIcon from '@mui/icons-material/HourglassEmptyOutlined'

interface IProps {
  userData: IUserDataRow
  refetch: () => void
  requested: boolean
  onRequested: () => void
}

type FormData = {
  withParkingAccess: boolean
  carPlate: string
  comment: string
  attachment: string
}

const HIDCreateAccount = ({
  userData,
  refetch,
  requested,
  onRequested,
}: IProps) => {
  const { t } = useTranslation(['hid'])
  const [errorMessage, setErrorMessage] = useState('')

  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<FormData>()

  const successNotify = (message: string) => toast.dark(message)
  const { login } = userData

  const onCompleted = (data: ICreateOrigoAccountData) => {
    const { status, error } = data.CreateMobileUserAccount
    if (status) {
      successNotify(t('success_message'))
      onRequested()
      refetch()
    } else {
      error && setErrorMessage(error)
    }
  }

  const [createHIDAccessAccount, { loading }] = useMutation<
    ICreateOrigoAccountData,
    ICreateOrigoAccountVars
  >(CREATE_ORIGO_ACCOUNT, {
    onCompleted,
    onError: (error: ApolloError) => setErrorMessage(error.message),
  })

  const handleCreateAccount = async (data: FormData) => {
    const { withParkingAccess, carPlate, comment, attachment } = data
    let attachmentBase64: any
    if (attachment) {
      await getBase64(attachment).then(res => {
        attachmentBase64 = res || ''
      })
    }
    await createHIDAccessAccount({
      variables: {
        login,
        withParkingAccess,
        carPlate,
        comment,
        attachment: String(attachmentBase64 || ''),
      },
    })
  }

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '12px',
          py: '32px',
        }}
      >
        <CircularProgress size={32} sx={{ color: colors.navy }} />
        <Typography
          sx={{ fontSize: '13.5px', fontWeight: 600, color: colors.textMuted }}
        >
          {t('account_under_creation')}
        </Typography>
      </Box>
    )
  }

  if (requested) {
    return (
      <Box
        data-e2e="mobile-card-requested"
        sx={{
          display: 'flex',
          gap: '12px',
          alignItems: 'flex-start',
          p: '14px 16px',
          borderRadius: '12px',
          background: '#FFF8E6',
          border: '1.5px solid #F9D06B',
        }}
      >
        <HourglassEmptyOutlinedIcon
          sx={{ fontSize: 20, color: '#F1901D', flexShrink: 0, mt: '1px' }}
        />
        <Box>
          <Typography
            sx={{ fontSize: '13.5px', fontWeight: 700, color: '#9A5A00' }}
          >
            {t('card_requested')}
          </Typography>
          <Typography
            sx={{ fontSize: '12.5px', color: '#B06E10', lineHeight: 1.55, mt: '3px' }}
          >
            {t('card_requested_desc')}
          </Typography>
        </Box>
      </Box>
    )
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      <Box>
        <Typography
          sx={{
            fontSize: '11px',
            fontWeight: 700,
            color: colors.slate,
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            mb: '14px',
          }}
        >
          {t('card_settings')}
        </Typography>

        <CardSettingsForm control={control} errors={errors} watch={watch} />
      </Box>

      <Box
        component="button"
        onClick={handleSubmit(handleCreateAccount)}
        data-e2e="create-mobile-card"
        sx={{
          width: '100%',
          height: 46,
          borderRadius: colors.radiusBtn,
          border: 'none',
          background: colors.navy,
          color: '#fff',
          fontWeight: 700,
          fontSize: '14px',
          fontFamily: 'inherit',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '7px',
          cursor: 'pointer',
          boxShadow: colors.shadowBtn,
          transition: 'background .15s',
          '&:hover': { background: colors.navyDark },
        }}
      >
        <AddIcon sx={{ fontSize: 17 }} />
        {t('create_card')}
      </Box>

      {errorMessage && <Error errorMessage={errorMessage} />}
    </Box>
  )
}

export default HIDCreateAccount
